import { zodResolver } from "@hookform/resolvers/zod";
import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  CircularProgress,
  Grid2,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getManagementUnitTypes } from "../../api/managementUnits";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { useAccountPermissions } from "../../hooks/Permissions/useAccountPermissions";
import { useEntityAccessPermissions } from "../../hooks/Permissions/useEntityAccessPermissions";
import { useFormStyles } from "../../hooks/useFormStyles";
import { getManagementUnitFormSchema, ManagementUnitForm as ManagementUnitFormType } from "../../types/forms/managementUnit";
import { ManagementUnit, ManagementUnitType } from "../../types/model/managementUnit";

interface ManagementUnitInfoProps {
  entityId: number;
  managementUnitData: ManagementUnit;
  onSubmit: (values: ManagementUnitFormType) => Promise<void>;
  isUpdating?: boolean;
}

export default function ManagementUnitInfo ({ entityId, managementUnitData, onSubmit, isUpdating }: Readonly<ManagementUnitInfoProps>) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  
  const { canEditData } = useAccountPermissions();
  const {
    canEditEntity,
    isLoading: isLoadingEntityPermissions,
  } = useEntityAccessPermissions();

  const canEdit =
    canEditData &&
    !isLoadingEntityPermissions &&
    canEditEntity(entityId);

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [managementUnitTypes, setManagementUnitTypes] = useState<ManagementUnitType[]>([]);
  const formStyles = useFormStyles(isEditing);

  const validationSchema = getManagementUnitFormSchema(t);
  const methods = useForm<ManagementUnitFormType>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      name: managementUnitData?.name || "",
      notes: managementUnitData?.notes || "",
      unitTypeId: managementUnitData?.unitTypeId ?? undefined,
    },
  });

  useEffect(() => {
    const fetchManagementUnitTypes = async () => {
      try {
        const types = await getManagementUnitTypes();
        setManagementUnitTypes(types);
      } catch (error) {
        handleError(error);
      }
    };

    fetchManagementUnitTypes();
  }, []);

  const handleFormSubmit = async (values: ManagementUnitFormType) => {
    try {
      setIsSaving(true);
      await onSubmit(values);
      setIsEditing(false);
    } catch (error) {
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12 }}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Grid2 size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 3,
                    }}
                  >
                    <Typography variant="h5">
                      {t("management-units.management-unit.general.information")}
                    </Typography>

                    {/* Action Icons */}
                    {isEditing && canEdit ? ( 
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title={t("generic.save")}>
                          <IconButton
                            color="success"
                            onClick={methods.handleSubmit(handleFormSubmit)}
                            disabled={isSaving || isUpdating}
                          >
                            {isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={t("generic.cancel")}>
                          <IconButton
                            color="error"
                            onClick={() => {
                              setIsEditing(false);
                              methods.reset();
                            }}
                            disabled={isSaving || isUpdating}
                          >
                            <EditOffIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      canEdit && (
                        <Tooltip title={t("generic.edit")}>
                          <IconButton
                            color="primary"
                            onClick={() => setIsEditing(true)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )
                    )}
                  </Box>
                </Grid2>
                <Grid2 container spacing={2} mt={1}>
                  <Grid2 size={8}>
                    <TextField
                      fullWidth
                      label={`${t("management-units.management-unit.general.name")} *`}
                      variant={isEditing ? "outlined" : "standard"}
                      disabled={!isEditing || isUpdating || isSaving}
                      sx={{ ...formStyles }}
                      {...methods.register("name")}
                      error={!!methods.formState.errors.name}
                      helperText={methods.formState.errors.name?.message}
                    />
                  </Grid2>
                  <Grid2 size={4}>
                    <Controller
                      name="unitTypeId"
                      control={methods.control}
                      render={({ field }) => (
                        <TextField
                          select
                          fullWidth
                          label={`${t("management-units.management-unit.general.type")} *`}
                          variant={isEditing ? "outlined" : "standard"} 
                          disabled={!isEditing || isUpdating || isSaving}
                          sx={{ ...formStyles }}
                          value={
                            managementUnitTypes.some((o) => o.id === field.value)
                              ? field.value
                              : ""
                          }
                          onChange={(e) =>
                            field.onChange(e.target.value ? Number(e.target.value) : null)
                          }
                          error={!!methods.formState.errors.unitTypeId}
                          helperText={methods.formState.errors.unitTypeId?.message}
                        >
                          <MenuItem value="">
                            —
                          </MenuItem>

                          {managementUnitTypes.map((type) => (
                            <MenuItem key={type.id} value={type.id}>
                              {t(type.i18nLabel)}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label={t("management-units.management-unit.general.notes")}
                      placeholder={t("management-units.management-unit.general.notes-placeholder")}
                      variant={isEditing ? "outlined" : "standard"}
                      disabled={!isEditing || isUpdating || isSaving}
                      multiline
                      minRows={6}
                      sx={{ ...formStyles }}
                      {...methods.register("notes")}
                    />
                  </Grid2>
                </Grid2>
              </Box>
            </Paper>
          </Grid2>
        </Grid2>
      </Box>
    </FormProvider>
  );
};